import type { TFunction } from 'i18next';
import { needsReview, type LiveAttendanceRow } from '../../api/live.api.js';
import type { OperationsSite } from './operationsModel.js';

/**
 * THE EXCEPTIONS THE KPI STRIP COUNTS — one predicate each, and the SAME
 * predicate does the counting and the filtering.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A FILE AND NOT TWO ARROW FUNCTIONS AT THE CALL SITES
 *
 * A tile that says 12 and opens a list of 9 is worse than a tile that opens
 * nothing: the first one is a board lying about its own arithmetic, and the
 * reader has no way to tell which of the two numbers to act on. It is also the
 * easiest defect in this whole screen to introduce, because the count and the
 * filter live in different files and read the same feed through different
 * expressions — `status === 'NOT_PUNCHED'` in one, `!== 'IN'` in the other, and
 * the two agree until somebody punches out.
 *
 * So the guarantee is structural rather than reviewed. `operationsKpis` counts
 * with these functions and `PeoplePanel` filters with these functions, over the
 * same `/attendance/live` array. The count IS the length of the landing set,
 * for the same reason `[1,2,3].filter(p).length` is the number of elements
 * `filter(p)` returns — not because two people wrote the same condition twice.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY NOT HERE
 *
 * **Absent today.** It reads like the third exception on the strip and it is
 * not linkable, because it is not counted from this feed at all: it is the sum
 * of `absent` over `/worksites/board/manpower`, which is a DAY-level reckoning
 * that knows about weekends and holidays and is keyed by site, not by person.
 * There is no roster of the individuals behind it anywhere on this page, so a
 * link would have to land on a list assembled by a different rule — the exact
 * count-versus-destination gap this file exists to prevent. It stays a figure.
 *
 * **On site now** and **On the clock**. Neither names an exception; they are
 * the two healthy populations, and a board where every tile is a button trains
 * an operator to press the ones that do not matter. `isOnClock` below exists
 * anyway, for the same reason the three exceptions do: the People panel's
 * roster card is titled "On the clock" and has to actually show that
 * population rather than the whole roster under a label that does not
 * describe it — see the note on `isOnClock`.
 */

/** The exceptions that can be filtered out of the live per-person roster. */
export type PeopleException = 'notPunched' | 'flagged' | 'unaccounted';

/** What every exception is: a question asked of one row of the live board. */
export type PeoplePredicate = (row: LiveAttendanceRow) => boolean;

/**
 * The exceptions decidable from the ROW ALONE.
 *
 * `flagged` reads `lastEventStatus` and not `status`: a flag is a SECOND axis
 * on a row, not a fourth value of the first one — a flagged scan can belong to
 * someone who is in, out, or (via a rejected punch) not punched at all. The KPI
 * strip has always counted it that way; this is that same rule, written once.
 *
 * `unaccounted` is deliberately NOT in here, and the difference is meaningful
 * rather than an inconsistency: it is the only exception that is not a property
 * of the row. It is a property of the row AGAINST A CLOCK, in the company's
 * timezone, so it cannot be a `(row) => boolean` sitting in a constant. It is
 * reached through `peopleExceptionPredicate` below, which is the single door
 * both the counting and the filtering go through for all three.
 */
export const PEOPLE_EXCEPTIONS: Record<
  'notPunched' | 'flagged',
  PeoplePredicate
> = {
  notPunched: (row) => row.status === 'NOT_PUNCHED',
  flagged: (row) => needsReview(row),
};

/**
 * The healthy population "On the clock" names — kept OUT of `PEOPLE_EXCEPTIONS`
 * on purpose (see the module header): it is not a toggle a reader switches on
 * to narrow a queue, it is the base population the roster card under that
 * heading has to actually be.
 *
 * Read by BOTH `operationsKpis` (the tile's own count) and `PeoplePanel` (the
 * roster card's base filter) so the two can never drift apart the way the
 * card's heading and its rows once did.
 */
export function isOnClock(row: LiveAttendanceRow): boolean {
  return row.status === 'IN';
}

// ─── Unaccounted ──────────────────────────────────────────────────────────────

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * "LEFT SOMEWHERE, ARRIVED NOWHERE" — the third exception, and the only one on
 * this board that is an INFERENCE rather than a record.
 *
 * The owner's words: *"maybe if labour going out from camp and not logged into
 * sites or transferred from sites and still not logged into other sites"*. The
 * rule below is that sentence and nothing more:
 *
 *   the last event today is a PUNCH_OUT       — they left
 *   it was more than 120 minutes ago          — long enough to matter
 *   their working day is still running        — so it is not just home time
 *
 * READ THIS BEFORE LOOSENING ANY OF THE THREE.
 *
 * **Why a PUNCH_OUT and not `status === 'OUT'`.** `status` says `'OUT'` for
 * four different facts, and three of them are fine: a camp PUNCH_IN means the
 * worker has ARRIVED at accommodation (the live board deliberately scores that
 * as off-site, not as missing), and BREAK_START / BREAK_END are a person
 * standing exactly where they were. Counting those would put people we can see
 * on a list of people we have lost.
 *
 * **Why 120 minutes.** It is the number the owner gave, and it also has to
 * survive the field APK, which is OFFLINE-CAPABLE and BUFFERS punches: "no
 * check-in recorded" can perfectly well mean "recorded on the device, not yet
 * synced". Two hours sits far above that buffer, which is what makes the
 * threshold defensible rather than arbitrary — and it is why the copy on the
 * row says what was OBSERVED ("no check-in recorded since 08:12") instead of
 * accusing anyone of being missing. A worker whose punch is still queued is a
 * real person who will otherwise be phoned about a problem that does not
 * exist, and the wording has to stay true when that is the case.
 *
 * **Why the working day has to still be running.** Without it, everyone who
 * finishes at 17:00 becomes "unaccounted" at 19:00, the board cries wolf every
 * single evening, and operators learn to ignore the tile. That is strictly
 * worse than not shipping this at all.
 *
 * **What it never says.** `/api/field/transfers` writes the SITE_OUT and the
 * SITE_IN in ONE transaction at the SAME timestamp, so an operator-driven
 * transfer cannot strand anybody. A stranded site-OUT therefore came from an
 * ordinary punch, and nothing here describes it as a failed transfer.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const UNACCOUNTED_AFTER_MINUTES = 120;

/**
 * The clock the observation is measured against.
 *
 * One instant for the whole screen (rule 3.1), sampled by the page and passed
 * down, so the tile's count and the roster's rows can never be measured a
 * second apart and disagree about a row sitting on the boundary. `timeZone` is
 * the COMPANY zone, because the working-day bounds are wall-clock times in it —
 * the browser's zone is a viewing preference and must not decide a boundary
 * (docs/time-and-dates.md §1).
 */
export interface ExceptionClock {
  now: Date;
  timeZone: string;
}

/** Where they were last seen. Two facts, not equally urgent — see below. */
export type UnaccountedKind = 'camp' | 'site';

/** The observation itself, in the terms the row will state it. */
export interface Unaccounted {
  /**
   * `camp` — they left accommodation and have not arrived at a worksite. They
   *          set off for work and never got there; unambiguous.
   * `site`  — they left a worksite and have arrived nowhere. Could equally be
   *          an early finish, so it is the weaker of the two and is worded
   *          that way.
   */
  kind: UnaccountedKind;
  /** ISO instant of the PUNCH_OUT — the thing that WAS recorded. */
  since: string;
  /** Whole minutes from that punch to `clock.now`. Always > the threshold. */
  minutes: number;
}

/** "HH:MM" → minutes past midnight, or null for anything unparseable. */
export function parseHhmm(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * The current wall clock in `timeZone`, as minutes past midnight.
 *
 * `Intl.formatToParts` with `hourCycle: 'h23'` rather than the device's own
 * `getHours()`: this number is compared against shift bounds the COMPANY set,
 * so it has to be read on the company's clock. A roaming admin — and this
 * product has a per-user flag for exactly that — would otherwise be told a
 * different story about the same worker than the operator sitting on site.
 */
export function minutesOfDayInZone(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: 'hour' | 'minute'): number =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const h = get('hour');
  const m = get('minute');
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  // 'h23' should never produce 24, but a stray engine that does would otherwise
  // put midnight an entire day past every evening bound.
  return (h % 24) * 60 + m;
}

/**
 * Is this person's working day still running at `nowMinutes`?
 *
 * THREE ANSWERS, NOT TWO. `null` means the bounds do not permit an answer, and
 * it is not the same as `false`: a tenant with no stored workday, or a shift
 * whose two ends are identical, tells us nothing about whether anyone is due —
 * and rule 3.3 says an unknown must not be spent as though it were a finding.
 *
 * The overnight case is handled rather than dropped, because a labour camp
 * running nights is exactly the population this observation is for: when the
 * end is at or before the start the window wraps midnight, and "still running"
 * means at-or-after the start OR before the end.
 *
 * Requiring `now >= start` as well as `now < end` costs one real case — a
 * worker who leaves camp before their day begins is not reported until it does
 * — and that is the right trade: it is a DELAY of at most the pre-shift gap,
 * not a miss, and the alternative is reporting people who are not yet due.
 */
export function isWorkingDayRunning(
  shiftStart: string | null | undefined,
  shiftEnd: string | null | undefined,
  nowMinutes: number
): boolean | null {
  const start = parseHhmm(shiftStart);
  const end = parseHhmm(shiftEnd);
  if (start === null || end === null) return null;
  if (!Number.isFinite(nowMinutes)) return null;
  if (start === end) return null;
  if (end > start) return nowMinutes >= start && nowMinutes < end;
  return nowMinutes >= start || nowMinutes < end;
}

/**
 * The observation for one row, or `null` for no claim.
 *
 * EVERY unknown returns `null`. A row from an API build that predates
 * `lastEventType` / `worksiteType`, a punch that matched no worksite at all
 * (remote staff, who have no site to arrive at and so cannot fail to), a
 * tenant with no working-day bounds, a `since` that will not parse: none of
 * those is evidence that anybody is missing, and none of them is allowed to
 * arrive on screen dressed as one.
 */
export function unaccountedOf(
  row: LiveAttendanceRow,
  clock: ExceptionClock
): Unaccounted | null {
  // 1. They LEFT. Not a camp arrival, not a break — see the block above.
  if (row.lastEventType !== 'PUNCH_OUT') return null;

  // 2. Long enough ago to be past the offline buffer.
  if (!row.since) return null;
  const at = new Date(row.since).getTime();
  if (Number.isNaN(at)) return null;
  // A duration, so milliseconds are the right unit (time-and-dates §4). It is
  // the DAY boundary below that must not be measured this way.
  const minutes = Math.floor((clock.now.getTime() - at) / 60_000);
  // Strictly greater: at exactly the threshold the wait is not yet over. A
  // negative age is a clock disagreement, not an observation.
  if (!(minutes > UNACCOUNTED_AFTER_MINUTES)) return null;

  // 3. We know what kind of place they left. Null is an older API build or a
  //    punch attached to no worksite; both mean no claim, never a guess.
  const type = row.worksiteType;
  if (type == null) return null;

  // 4. Their day is still running — measured on the COMPANY clock.
  const running = isWorkingDayRunning(
    row.shiftStart,
    row.shiftEnd,
    minutesOfDayInZone(clock.timeZone, clock.now)
  );
  if (running !== true) return null;

  return { kind: type === 'CAMP' ? 'camp' : 'site', since: row.since, minutes };
}

/**
 * THE ONE DOOR. Every count and every filter on this board resolves its
 * predicate here, so a tile's number is the length of the set its destination
 * shows by construction rather than by two files agreeing.
 *
 * `clock` is optional because two of the three exceptions do not need one, and
 * because a caller that cannot supply one must not silently get a different
 * answer: without a clock `unaccounted` matches NOTHING here, and
 * `countPeopleException` reports `null` rather than 0 for it — the two halves
 * of "we could not decide".
 */
export function peopleExceptionPredicate(
  key: PeopleException,
  clock?: ExceptionClock
): PeoplePredicate {
  if (key === 'unaccounted') {
    if (!clock) return () => false;
    return (row) => unaccountedOf(row, clock) !== null;
  }
  return PEOPLE_EXCEPTIONS[key];
}

/**
 * Can this feed answer the unaccounted question AT ALL?
 *
 * `unaccountedOf` returns `null` for a row from an API build that predates
 * `lastEventType`, which is right per row — but it AGGREGATES to a confident
 * zero, and "the API cannot tell us" arriving on the strip as "nobody is
 * unaccounted" is rule 3.3 failing at exactly the scale that matters. The two
 * halves of the product deploy separately on this product's own demo host, so
 * this window is real rather than theoretical.
 *
 * A current build always emits the field — `null` for somebody who has not
 * punched, a type for everybody who has — so `undefined` on EVERY row is the
 * signature of the old build, and a single defined value is proof of the new
 * one. An empty roster is a real, answerable zero and stays one.
 */
export function unaccountedIsDecidable(
  rows: readonly LiveAttendanceRow[]
): boolean {
  if (rows.length === 0) return true;
  return rows.some((row) => row.lastEventType !== undefined);
}

/**
 * How many rows match — `null` in, `null` out.
 *
 * Rule 3.3 in a function signature: an unread feed has no count, and it must
 * not arrive at the screen as a confident 0. There is no `?? []` here for the
 * same reason there is none in `operationsKpis`. `unaccounted` has two further
 * ways of not knowing, and both report `null` rather than nought: no clock
 * means the question was never asked, and a feed with none of the fields means
 * it cannot be answered.
 */
export function countPeopleException(
  rows: readonly LiveAttendanceRow[] | undefined,
  key: PeopleException,
  clock?: ExceptionClock
): number | null {
  if (!rows) return null;
  if (key === 'unaccounted' && (!clock || !unaccountedIsDecidable(rows))) return null;
  const predicate = peopleExceptionPredicate(key, clock);
  let n = 0;
  for (const row of rows) if (predicate(row)) n += 1;
  return n;
}

/**
 * The site exception: a worksite the map cannot draw AND shouldn't be
 * expected to.
 *
 * Same contract as the people pair — `operationsKpis.unmappedSites` counts with
 * this and `SitesPanel`'s missing-coordinates filter filters with it, over the
 * same merged rows. It starts from the negation of `mappable` (the "both
 * coordinates parsed" test from `buildOperationsSites`), but excludes `REMOTE`
 * sites: a remote-work bucket has no coordinates BY DESIGN, not by omission,
 * so counting it here would flag a data-quality warning at an admin who did
 * nothing wrong. It still carries `mappable: false` and never gets a pin —
 * this only keeps it out of the "you forgot to geocode this" exception.
 */
export function isUnmappedSite(site: OperationsSite): boolean {
  return !site.mappable && site.type !== 'REMOTE';
}

/** The unmapped rows, in the order they were given. */
export function unmappedSites(
  sites: readonly OperationsSite[]
): OperationsSite[] {
  return sites.filter(isUnmappedSite);
}

/* ===========================================================================
 * WHAT THE PEOPLE TABLE IS ACTUALLY LISTING, as a heading.
 *
 * The table was headed "On the clock" whatever the filters said, and the
 * predicate beside it already knew better: it drops the on-the-clock
 * restriction the moment `notPunched` or `unaccounted` is switched on, because
 * both of those are searches for people who by definition are NOT on the clock.
 * So the one state where the heading was reliably wrong was the state a
 * supervisor reaches by pressing an exception tile — and the screenshot that
 * started this audit is exactly that: a table headed "On the clock" over a
 * single row reading `Out`, beneath a tile reading "On the clock 0".
 *
 * Three titles for three populations, from the same two booleans the filter
 * reads, so the heading cannot come to describe a different list from the one
 * under it (§3.2). `unaccounted` outranks `notPunched` because the two are
 * mutually exclusive on any real row — a worker with no punch today cannot
 * have left somewhere — so the order only decides a state the data cannot
 * produce, and picking one keeps the heading total.
 * ======================================================================== */
export function peopleListTitle(
  t: TFunction,
  filters: { notPunchedOnly: boolean; unaccountedOnly: boolean }
): string {
  if (filters.unaccountedOnly) {
    return t('operations.people.listUnaccounted', 'Unaccounted');
  }
  if (filters.notPunchedOnly) {
    return t('operations.people.listNotPunched', 'Not punched');
  }
  return t('operations.people.onTheClock', 'On the clock');
}
